import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './index.css';
import DeviceFrame from './components/DeviceFrame';
import Actions from './components/Actions';
import HelpModal from './components/HelpModal';
import LiveModal from './components/LiveModal';
import SettingsModal from './components/SettingsModal';
import BrandSwitcher from './components/BrandSwitcher';
import FooterCarousel from './components/FooterCarousel';
import { ToastContainer } from './components/ToastNotification';
import LandingPage from './components/landing/LandingPage';
import DemosPage from './components/DemosPage';
import AdminDashboard from './components/admin/AdminDashboard';
import LoadingScreen from './components/LoadingScreen';
import GuidedTour from './components/GuidedTour';
import { useSelector, useDispatch } from 'react-redux';
import useWebSocket from './hooks/useWebSocket';
import { fetchConfig, setFollowMode, setCalendarDate, setPegaContextID, updateConfig, setChannel, initializeFromDatabase, selectHasSingleBrandAndStory, fetchAvailableConfigurations, fetchAvailableBrands, goBack, setCallInProgress, setCallType, setOnboardingCallEvent, setOnboardingTourSteps, setSettingsModalOpen, setOnboardingDefaultTourPending, clearDynamicState } from './features/channelSlice';
import { clearNotifications } from './features/notificationSlice';
import { setDevice } from './features/deviceSlice';
import { signOut } from './features/authSlice';
import ChannelRedirect from './components/ChannelRedirect';
import { toggleSidePanel, setWindowWidth, clearEmbedHistory } from './features/sidePanelSlice';
import SidePanel from './components/SidePanel';
import { setPegaConversationID } from './features/channelSlice';
import { setOnboardingMode, clearLiveEvents, setLiveVideo, openLiveModal, setCurrentEventIndex, addEventToHistory } from './features/liveSlice';

// Authentication components
import AuthContainer from './components/auth/AuthContainer';

import { initializeResizeObserverErrorHandling } from './utils/resizeObserverErrorHandler';
import { shouldUseMobileUI } from './utils/deviceUtils';
import tokenManager from './utils/tokenManager';
import authService from './services/authService';
import { isCaraBridgeAvailable, sendCaraStart, sendCaraStop, sendCaraQuit } from './utils/caraBridge';
import { teardownExperience } from './utils/resetExperience';

// Initialize ResizeObserver error handling globally
initializeResizeObserverErrorHandling();

// Fix for mobile viewport height (especially Samsung Galaxy S25+ and other Android devices)
// The 100vh CSS unit doesn't account for browser UI (address bar) on mobile
// This sets a CSS custom property --app-height with the actual viewport height
// Width of the fixed left action-button toolbar (.desktop-actions-fixed).
// Reserved on the left of the device area and subtracted from the side-panel
// width so the phone never slides under the toolbar (desktop / iPad-desktop).
const LEFT_TOOLBAR_WIDTH = 80;

const setAppHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
};

// Set initial value and update on resize/orientation change
setAppHeight();
window.addEventListener('resize', setAppHeight);
window.addEventListener('orientationchange', () => {
  // Delay to allow browser UI to settle after orientation change
  setTimeout(setAppHeight, 100);
});

// Nuclear option: Completely suppress ResizeObserver errors
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Override ResizeObserver globally with error-safe version
if (window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback) {
      const safeCallback = debounce((entries) => {
        requestAnimationFrame(() => {
          try {
            callback(entries);
          } catch (error) {
            // Silently ignore all ResizeObserver errors
          }
        });
      }, 16);
      super(safeCallback);
    }
  };
}

// Additional comprehensive ResizeObserver error suppression
const originalWindowError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  if (message && typeof message === 'string' && 
      (message.includes('ResizeObserver loop completed with undelivered notifications') ||
       message.includes('ResizeObserver loop limit exceeded'))) {
    return true; // Suppress the error
  }
  if (originalWindowError) {
    return originalWindowError.call(window, message, source, lineno, colno, error);
  }
  return false;
};

// Moved contextID logic directly into App.js (centralized from utils/contextId.js)
const getOrCreateContextID = () => {
  let contextID = localStorage.getItem('pegaContextID');
  if (!contextID) {
    contextID = crypto.randomUUID();
    localStorage.setItem('pegaContextID', contextID);
  }
  return contextID;
};


function MainApp() {
  const location = useLocation();

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showBrandSwitcherModal, setShowBrandSwitcherModal] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? shouldUseMobileUI() : false);
  const iPadDesktopLandscape = useSelector((state) => state.channel.iPadDesktopLandscape);
  // Re-evaluate isMobile the instant the iPad-landscape-desktop trial flag
  // flips. The reducer's synthetic `resize` is timing-sensitive (it can fire
  // before React has committed the new redux state), so we observe the flag
  // directly here as a belt-and-braces sync path.
  useEffect(() => {
    setIsMobile(shouldUseMobileUI());
  }, [iPadDesktopLandscape]);
  const [agentMode, setAgentMode] = useState(false); // NEW: agent mode state
  const [isEnvironmentOffline, setIsEnvironmentOffline] = useState(false); // Pega environment health check
  const [toastMessages, setToastMessages] = useState([]); // Toast notification stack
  const dispatch = useDispatch();
  const configStatus = useSelector((state) => state.channel.configStatus);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const hasSingleBrandAndStory = useSelector(selectHasSingleBrandAndStory);
  const configId = useSelector((state) => state.channel.configId);
  const pegaEnabled = useSelector((state) => state.channel.pegaEnabled);
  const toastsEnabled = useSelector((state) => state.notifications.toastsEnabled);
  const currentChannel = useSelector((state) => state.channel.currentChannel);
  const isCallInProgress = useSelector((state) => state.channel.isCallInProgress);
  const channelConfig = useSelector((state) => state.channel.channelConfig);
  
  // Track if we're currently transitioning from SettingsModal to prevent re-opening
  const isTransitioningRef = React.useRef(false);
  // Tracks whether the device frame has been rendered to the user at least
  // once. Used to keep the device visible behind later SettingsModal openings
  // (so it doesn't blink away) while still hiding it for the first-launch
  // modal (where it would otherwise flash before the modal fades in).
  const hasShownDeviceRef = React.useRef(false);
  useEffect(() => {
    if (isInitialized && !showSettingsModal && !isTransitioningRef.current) {
      hasShownDeviceRef.current = true;
    }
  });
  
  // Track if we've loaded onboarding events for this config to prevent duplicates
  const loadedOnboardingConfigRef = React.useRef(null);

  // Debug logging
  React.useEffect(() => {
    console.log('🎯 App.js - hasSingleBrandAndStory changed:', hasSingleBrandAndStory);
  }, [hasSingleBrandAndStory]);

  // Use the unified WebSocket status
  const { webSocketStatus, signalStrength, forceReconnectAll } = useWebSocket();

  useEffect(() => {
    window.CaraIOSBridge = {
      isAvailable: isCaraBridgeAvailable,
      sendCaraStart,
      sendCaraStop,
      sendCaraQuit,
    };

    console.log('[CaraBridge] window.CaraIOSBridge registered. Native bridge available:', isCaraBridgeAvailable());

    return () => {
      delete window.CaraIOSBridge;
    };
  }, []);

  // Default WebSocket statuses to 'pending' if config is not loaded
  const effectiveCustomerStatus = configStatus === 'loaded' ? webSocketStatus.customerStatus : 'pending';
  const effectiveLiveStatus = configStatus === 'loaded' ? webSocketStatus.liveStatus : 'pending';
  const effectiveSignalStrength = configStatus === 'loaded' ? signalStrength : 0;

  // Token monitoring effect
  useEffect(() => {
    if (!isAuthenticated) {
      tokenManager.stopMonitoring();
      return;
    }

    // Start monitoring token expiration
    tokenManager.startMonitoring(
      // onTokenExpired callback
      () => {
        console.warn('🔐 Token expired, signing out user');
        dispatch(signOut());
      },
      // onTokenRefreshed callback
      () => {
        console.log('🔐 Token refreshed, reconnecting WebSockets');
        
        // Play refresh sound
        setTimeout(() => {
          const audio = new Audio('/sounds/done.mp3');
          audio.volume = 0.2; // Quieter for token refresh
          audio.play().catch(err => console.log('Could not play refresh sound:', err));
        }, 100);
        
        // Force WebSocket reconnection with new token
        setTimeout(() => {
          forceReconnectAll();
        }, 500);
      }
    );

    return () => {
      tokenManager.stopMonitoring();
    };
  }, [isAuthenticated, dispatch, forceReconnectAll]);

  // Monitor config loading failures and set offline state
  useEffect(() => {
    if (configStatus === 'failed') {
      console.error('❌ Config failed to load - marking environment as offline');
      setIsEnvironmentOffline(true);
    } else if (configStatus === 'loaded') {
      // Config loaded successfully - environment is likely online
      // (will be double-checked by health check)
      console.log('✅ Config loaded - environment appears online');
      setIsEnvironmentOffline(false);
    }
  }, [configStatus]);

  // Environment health check - proxied via backend, which can reach Pega from inside the IP allowlist.
  // Backend endpoint: GET /api/v1/health/pega?config_id={id} → 200 { online: bool }
  useEffect(() => {
    const checkEnvironmentHealth = async () => {
      // Skip the Pega health check entirely for non-Pega configs.
      if (!configId || !pegaEnabled) {
        setIsEnvironmentOffline(false);
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const token = await authService.getToken();
        const response = await fetch(
          `${API_BASE_URL}/api/v1/health/pega?config_id=${encodeURIComponent(configId)}`,
          {
            method: 'GET',
            signal: controller.signal,
            cache: 'no-cache',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        clearTimeout(timeoutId);

        if (response.status === 404) {
          // Backend hasn't shipped the endpoint yet — don't degrade UX
          console.warn('⚠️ Health endpoint not available on backend (404). Assuming online.');
          setIsEnvironmentOffline(false);
          return;
        }

        if (!response.ok) {
          console.error('❌ Health check backend error:', response.status);
          setIsEnvironmentOffline(true);
          return;
        }

        const data = await response.json().catch(() => null);
        const online = data?.online !== false; // default to online unless explicitly false
        console.log(online ? '✅ Pega online' : '❌ Pega offline (per backend)');
        setIsEnvironmentOffline(!online);
      } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          console.error('❌ Health check timed out');
        } else {
          console.error('❌ Health check failed:', err.message);
        }
        setIsEnvironmentOffline(true);
      }
    };

    window.testOfflineMode = (offline = true) => {
      console.log(offline ? '🔴 Testing OFFLINE mode' : '🟢 Testing ONLINE mode');
      setIsEnvironmentOffline(offline);
    };

    if (configId && pegaEnabled) {
      checkEnvironmentHealth();
      const healthCheckInterval = setInterval(checkEnvironmentHealth, 300000);
      return () => clearInterval(healthCheckInterval);
    }
  }, [configId, pegaEnabled]);

  // Reset onboarding tracking when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('🔐 User logged out - resetting onboarding tracking ref');
      loadedOnboardingConfigRef.current = null;
      setIsInitialized(false);
    }
  }, [isAuthenticated]);

  // Helper function to setup onboarding events immediately - wrapped in useCallback for stable reference
  const setupOnboardingEventsNow = React.useCallback((currentConfigId, currentOnboardingEvents) => {
    const resolveCallType = (requested) => requested || 'voice';
    // Skip if we've already loaded onboarding for this config
    if (loadedOnboardingConfigRef.current === currentConfigId) {
      console.log('⏭️ Skipping onboarding load - already loaded for config:', currentConfigId);
      return;
    }
    
    // Load onboarding events into live history
    if (currentOnboardingEvents && currentOnboardingEvents.length > 0) {
      console.log('🎓 Loading onboarding events for config:', currentOnboardingEvents.length, 'events');

      const callEvent = currentOnboardingEvents.find(e => e.type === 'call') || null;
      const tourEvent = currentOnboardingEvents.find(e => e.type === 'tour') || null;
      // `once: true` on a default-tour event means: skip if we've already shown
      // the built-in tour to this user (tracked via the 'guidedTourShown' key).
      const rawDefaultTourEvent = currentOnboardingEvents.find(e => e.type === 'default-tour') || null;
      const tourAlreadyShown = localStorage.getItem('guidedTourShown') === 'true';
      const defaultTourEvent = (rawDefaultTourEvent && rawDefaultTourEvent.once && tourAlreadyShown)
        ? null
        : rawDefaultTourEvent;
      if (rawDefaultTourEvent && !defaultTourEvent) {
        console.log('🧭 Skipping default-tour onboarding event (once=true, already shown)');
      }
      // Loose tour-shaped entries: any onboarding event with a `target` but no
      // recognised media `type` is treated as an individual tour step.
      const looseTourSteps = currentOnboardingEvents.filter(
        e => e.target && e.type !== 'call' && e.type !== 'tour' && e.type !== 'default-tour' && e.type !== 'image' && e.type !== 'video'
      );
      const mediaEvents = currentOnboardingEvents.filter(
        e => (e.type === 'image' || e.type === 'video') && !e.target
      );

      const tourSteps = [
        ...(tourEvent?.steps?.length > 0 ? tourEvent.steps : []),
        ...looseTourSteps,
      ];

      // Store call and default-tour intents; tour steps are dispatched AFTER
      // openLiveModal (below) so GuidedTour never sees a render where steps
      // are queued but the modal isn't yet open — that race renders the nudge
      // briefly against an empty background.
      dispatch(setOnboardingCallEvent(callEvent));
      dispatch(setOnboardingDefaultTourPending(!!defaultTourEvent));
      const queuedTourSteps = tourSteps.length > 0 ? tourSteps : null;

      if (mediaEvents.length > 0) {
        // Show media events first; call (if any) triggers when user clicks Continue
        const firstEvent = mediaEvents[0];
        console.log('📺 Setting videoInfo for first onboarding event:', firstEvent.channel);
        dispatch(setLiveVideo({ ...firstEvent, isOnboardingEvent: true }));
        dispatch(setOnboardingMode(true));
        dispatch(setCurrentEventIndex(0));
        dispatch(openLiveModal());
        dispatch(setOnboardingTourSteps(queuedTourSteps));

        // Remaining events: add to history after LiveModal's useEffect has added the first one
        if (mediaEvents.length > 1) {
          setTimeout(() => {
            mediaEvents.slice(1).forEach(event => {
              dispatch(addEventToHistory({ ...event, isOnboardingEvent: true }));
            });
          }, 100);
        }

        console.log('✅ Onboarding event videoInfo set and modal opened');
      } else if (callEvent) {
        // No media events — start the call immediately
        console.log('📞 Onboarding: starting outbound call directly');
        dispatch(setCallType(resolveCallType(callEvent.callType)));
        dispatch(setCallInProgress(true));
        dispatch(setChannel('call'));
        dispatch(setOnboardingTourSteps(queuedTourSteps));
      } else if (defaultTourEvent) {
        // No media or call — start the built-in guided tour straight away.
        // GuidedTour clears the pending flag when the tour ends, so any
        // waiting nudge stays gated until the user finishes/skips it.
        console.log('🧭 Onboarding: starting default guided tour directly');
        dispatch(setOnboardingTourSteps(queuedTourSteps));
        setTimeout(() => {
          window.startGuidedTour?.();
        }, 500);
      } else {
        // Pure-tour onboarding (steps only, no blocking surface) — set the
        // steps and let the nudge render against the home screen.
        dispatch(setOnboardingTourSteps(queuedTourSteps));
      }
    } else {
      // Config has NO onboarding events - explicitly clear videoInfo
      console.log('📺 Config has no onboarding events, clearing videoInfo');
      dispatch(setLiveVideo(null));
      dispatch(setOnboardingCallEvent(null));
      dispatch(setOnboardingTourSteps(null));
      dispatch(setOnboardingDefaultTourPending(false));
    }
    
    // Mark this config as loaded
    loadedOnboardingConfigRef.current = currentConfigId;
    console.log('✅ Marked config as loaded:', currentConfigId);
  }, [dispatch]); // setOnboardingTourSteps intentionally omitted — stable Redux action

  // Shared initialization core: fetch config+brand, apply onboarding, wait for DOM to settle.
  // Every auto-init path and the SettingsModal LAUNCH button use this.
  const loadConfigAndBrand = React.useCallback(async (configId, brandId, { skipReset = true } = {}) => {
    dispatch(clearLiveEvents());
    dispatch(clearEmbedHistory());
    await dispatch(initializeFromDatabase({ configId, brandId, skipReset })).unwrap();
    const fullState = await dispatch((_, getState) => getState());
    const channelState = fullState.channel || {};
    setupOnboardingEventsNow(channelState.configId, channelState.onboardingEvents || []);
    await new Promise(resolve => setTimeout(resolve, 300));
  }, [dispatch, setupOnboardingEventsNow]);

  // Track if initialization has been attempted for this auth session
  const initializationAttemptedRef = React.useRef(false);

  // Reset initialization attempt flag when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      initializationAttemptedRef.current = false;
    }
  }, [isAuthenticated]);

  // Load config and brands for authenticated user
  useEffect(() => {
    const initializeApp = async () => {
      if (!isAuthenticated) return;
      if (initializationAttemptedRef.current) return;
      initializationAttemptedRef.current = true;

      // On error: always dismiss loading screen, fall back to SettingsModal
      const fallback = (error) => {
        console.error('❌ Failed to initialize app:', error);
        setIsInitialized(true);
        if (!isTransitioningRef.current) setShowSettingsModal(true);
      };

      try {
        const params = new URLSearchParams(window.location.search);
        const urlConfigId = params.get('config');
        const urlCustomerId = params.get('customer');
        const savedConfigId = localStorage.getItem('selectedConfigId');
        const savedBrandId = localStorage.getItem('selectedBrandId');

        if (urlConfigId && urlCustomerId) {
          // Both URL params supplied — full init, same as LAUNCH button
          try {
            await loadConfigAndBrand(+urlConfigId, +urlCustomerId);
            setIsInitialized(true);
          } catch (error) {
            fallback(error);
          }
        } else if (urlConfigId) {
          // Config-only URL param — use legacy fetchConfig path
          await dispatch(fetchConfig()).unwrap();
          await new Promise(resolve => setTimeout(resolve, 300));
          setIsInitialized(true);
        } else if (savedConfigId && savedBrandId) {
          // Returning user — restore saved selections
          try {
            await loadConfigAndBrand(parseInt(savedConfigId), parseInt(savedBrandId));
            setIsInitialized(true);
          } catch (error) {
            fallback(error);
          }
        } else {
          // First visit — check whether we can auto-init or need SettingsModal
          await dispatch(fetchAvailableConfigurations()).unwrap();
          await dispatch(fetchAvailableBrands()).unwrap();

          const fullState = await dispatch((_, getState) => getState());
          const { availableBrands = [], availableConfigurations = [] } = fullState.channel || {};

          if (availableBrands.length === 1 && availableConfigurations.length === 1) {
            const brandId = availableBrands[0]?.id;
            const configId = availableConfigurations[0]?.id;
            if (brandId && configId) {
              try {
                await loadConfigAndBrand(configId, brandId);
                setIsInitialized(true);
              } catch (error) {
                fallback(error);
              }
            } else {
              console.warn('⚠️ Missing brandId or configId for auto-init:', { brandId, configId });
              fallback(new Error('Missing ids'));
            }
          } else {
            // Multiple options — let the user choose
            setIsInitialized(true);
            if (!isTransitioningRef.current) setShowSettingsModal(true);
          }
        }
      } catch (error) {
        fallback(error);
      }
    };

    initializeApp();
  }, [isAuthenticated, dispatch, loadConfigAndBrand]);

  // --- Start in phone mode by default, but only if not already set ---
  useEffect(() => {
    if (typeof window !== 'undefined' && !localStorage.getItem('deviceType')) {
      dispatch(setDevice('phone'));
    }
  }, [dispatch]);
  // --- End phone mode default ---
  
  // Note: Onboarding events are now loaded inline during initialization
  // to ensure LiveModal appears immediately when the loading screen disappears
  // The setupOnboardingEventsNow function handles this in initializeApp

  // Add beforeunload event handler to prevent accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Skip confirmation for intentional redirects (e.g. SSO login)
      if (sessionStorage.getItem('intentional_redirect')) {
        sessionStorage.removeItem('intentional_redirect');
        return;
      }
      // Always show confirmation when user tries to navigate away from the app
      // This helps prevent accidental loss of session or data
      const confirmationMessage = 'Are you sure you want to leave? Your session and any unsaved progress will be lost.';
      
      event.preventDefault();
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // Remove location dependency - always show confirmation

  // Mobile back button: push browser history on channel change, listen for popstate
  const isPopstateNavRef = React.useRef(false);
  useEffect(() => {
    if (!isMobile || !currentChannel) return;
    // Don't push state if this change was triggered by popstate (back button)
    if (isPopstateNavRef.current) {
      isPopstateNavRef.current = false;
      return;
    }
    window.history.pushState({ channel: currentChannel }, '', window.location.pathname + window.location.search);
  }, [currentChannel, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    const handlePopState = (e) => {
      isPopstateNavRef.current = true;
      dispatch(goBack());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isMobile, dispatch]);

  // Auto-end call after 3 minutes when user navigates away from call channel
  const callTimeoutRef = React.useRef(null);
  useEffect(() => {
    if (!isCallInProgress) {
      // No active call — clear any pending timeout
      if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
      return;
    }
    const callChannelKey = Object.keys(channelConfig).find(k => channelConfig[k]?.type === 'call');
    const isOnCallChannel = currentChannel === callChannelKey;

    if (isOnCallChannel) {
      // User returned to call channel — cancel timeout
      if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
    } else {
      // User navigated away from call — start 3-minute timeout
      if (!callTimeoutRef.current) {
        console.log('📞 Call running in background — will auto-end in 3 minutes');
        callTimeoutRef.current = setTimeout(() => {
          console.log('📞 3-minute background call timeout reached — ending call');
          dispatch(setCallInProgress(false));
          callTimeoutRef.current = null;
        }, 3 * 60 * 1000);
      }
    }
    return () => {
      if (callTimeoutRef.current) { clearTimeout(callTimeoutRef.current); callTimeoutRef.current = null; }
    };
  }, [isCallInProgress, currentChannel, channelConfig, dispatch]);

  // NEW: Handle agent query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('agent') === 'true') {
      setAgentMode(true);
      // Open agent channel (simulate navigation)
      window.history.replaceState({}, '', '/agent' + window.location.search);
    }
  }, [location.search]);

  // Listen for toast notifications from WebSocket.
  // Lifecycle per toast (paced for visible cross-fades):
  //   t=0        pushed with state='in', any current 'in' flipped to 'out'
  //   t=7000     if still 'in', flip to 'out' (start exit animation)
  //   t=8000     removed from DOM entirely
  // The 800ms animation duration overlaps with the next toast's 800ms entrance
  // so two toasts visibly cross-fade.
  useEffect(() => {
    const handleToastNotification = (event) => {
      const { subject, body, icon } = event.detail;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      setToastMessages(prev => [
        ...prev.map(t => t.state === 'in' ? { ...t, state: 'out' } : t),
        // Default to the `auto_awesome` Material icon (a four-pointed sparkle)
        // to signal "the AI / agent is doing something." Callers can override
        // by passing an explicit icon name in the toast event.
        { id, subject, body, icon: icon || 'auto_awesome', state: 'in' },
      ]);

      // Auto-dismiss after 7s (only if not already pushed out by a newer toast)
      setTimeout(() => {
        setToastMessages(prev => prev.map(t =>
          t.id === id && t.state === 'in' ? { ...t, state: 'out' } : t
        ));
      }, 7000);

      // Remove from DOM at 8s — gives the 800ms exit animation time to play.
      // Toasts pushed out early sit invisible for the remainder, harmless.
      setTimeout(() => {
        setToastMessages(prev => prev.filter(t => t.id !== id));
      }, 8000);
    };

    window.addEventListener('toast-notification', handleToastNotification);
    return () => window.removeEventListener('toast-notification', handleToastNotification);
  }, []);

  // Handler to remove a specific toast (used by the mobile FooterCarousel
  // which renders close buttons).
  const removeToast = (id) => {
    setToastMessages(prev => prev.filter(toast => toast.id !== id));
  };

  const employeeVisible = useSelector((state) => state.channel.channelConfig.employee?.visible !== false);
  const employeeExpanded = useSelector((state) => state.sidePanel.sidePanelExpanded);
  const windowWidth = useSelector((state) => state.sidePanel.windowWidth);
  const deviceType = useSelector((state) => state.device.type);
  
  // Calculate side panel width for toast positioning
  const devicePxWidth = deviceType === 'phone' ? 380 : 820;
  const sidePanelWidth = employeeVisible && employeeExpanded
    ? Math.max(windowWidth - devicePxWidth - 200 - LEFT_TOOLBAR_WIDTH, 460)
    : 0;

  const isHelpModalOpen = useSelector((state) => state.channel.isHelpModalOpen);
  const isLiveModalOpen = useSelector((state) => state.live.isLiveModalOpen);
  
  // Check if any modal is open to prevent side panel from being opened over modals
  const isAnyModalOpen = isHelpModalOpen || isLiveModalOpen;
  
  // Close BrandSwitcher when SettingsModal opens
  useEffect(() => {
    if (showSettingsModal && showBrandSwitcherModal) {
      setShowBrandSwitcherModal(false);
    }
  }, [showSettingsModal, showBrandSwitcherModal]);
  
  // Sync settings modal state to Redux so GuidedTour can detect it
  useEffect(() => {
    dispatch(setSettingsModalOpen(showSettingsModal));
  }, [showSettingsModal, dispatch]);

  // NOTE: brand switcher open-state is reported by the BrandSwitcher component
  // itself (covers both the desktop Actions mount and the mobile App mount), so
  // no App-level sync is needed here.

  // Reflect the light/dark theme onto the document root so CSS can theme the
  // native Cara chrome (toolbar, side panel, modals) via [data-cara-theme].
  const caraTheme = useSelector((state) => state.device.theme);
  useEffect(() => {
    document.documentElement.setAttribute('data-cara-theme', caraTheme || 'dark');
  }, [caraTheme]);

  const gradientColor1 = useSelector((state) => state.channel.gradientColor1) || '#04142C';
  const gradientColor2 = useSelector((state) => state.channel.gradientColor2) || '#1e3a8a';
  const gradientColor3 = useSelector((state) => state.channel.gradientColor3) || '#23a6d5';
  const gradientColor4 = useSelector((state) => state.channel.gradientColor4) || '#23d5ab';
  const currentBrand = useSelector((state) => state.channel.currentBrand);
  const brandLogoUrl = currentBrand?.logoUrl || currentBrand?.brand_json?.logoUrl || null;
  const sidePanelIconSrc = useSelector((state) => state.channel.sidePanelIconSrc);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // When ?config= is present, initializeApp owns the full init sequence (including
    // fetchConfiguration, fetchBrand, applyBrand, etc.). Calling fetchConfig here too
    // would duplicate every API call and let config/fetch/fulfilled clobber the brand
    // state that channel/applyBrand already applied correctly.
    if (isAuthenticated && !params.get('config')) {
      dispatch(fetchConfig()).then((action) => {
        if (action.type === fetchConfig.fulfilled.type) {
          const loadedConfig = action.payload;
          // Handle applicationAlias parameter override
          const applicationAliasParam = params.get('applicationAlias');
          if (applicationAliasParam) {
            localStorage.setItem('xLabbApplicationAlias', applicationAliasParam);
            dispatch(updateConfig({
              ...loadedConfig,
              applicationAlias: applicationAliasParam
            }));
          }
        }
      });
    }

    const contextID = getOrCreateContextID();
    dispatch(setPegaContextID(contextID));

    // Set default channel to home
    dispatch((dispatch, getState) => {
      const state = getState();
      if (!state.channel.currentChannel || state.channel.currentChannel === '') {
        dispatch(setChannel('home'));
      }
    });

    // On first load, if calendarDate is null, set it to current system date/time
    dispatch((dispatch, getState) => {
      const state = getState();
      if (!state.channel.calendarDate) {
        dispatch(setCalendarDate(new Date().toISOString()));
      }
    });

    const followModeParam = params.get('followMode');
    if (followModeParam === 'true') {
      dispatch(setFollowMode(true));
    } else if (followModeParam === 'false') {
      dispatch(setFollowMode(false));
    }

    const handleResize = () => {
      dispatch(setWindowWidth(window.innerWidth));
      setIsMobile(shouldUseMobileUI());
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    window.scrollTo(0, 0);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [dispatch, location.search, isAuthenticated]);

  // When employee pane is expanded, switch to phone view
  useEffect(() => {
    if (employeeExpanded && !isMobile) {
      dispatch(setDevice('phone'));
    }
  }, [employeeExpanded, isMobile, dispatch]);

  // Removed automatic conversation initialization on app load.
  // Conversations should only start when user explicitly interacts:
  // 1. Clicking microphone in AgentView
  // 2. Starting a phone call in CallView (if AI is enabled)
  useEffect(() => {
    // Listen for reset/new events to clear conversation ID
    const handleClearConversation = (e) => {
      if (e.detail === 'reset' || e.detail === 'new') {
        dispatch(setPegaConversationID(null));
      }
    };
    window.addEventListener('pega-conversation-clear', handleClearConversation);
    return () => {
      window.removeEventListener('pega-conversation-clear', handleClearConversation);
    };
  }, [dispatch]);

  // Config values are now managed entirely through Redux state
  
  // Handler for quit/sign out functionality (for battery icon)
  const handleQuit = async () => {
    if (sendCaraQuit()) {
      teardownExperience({ dispatch });
      return;
    }

    // Check if authentication is enabled
    const authEnabled = process.env.REACT_APP_SKIP_AUTH !== 'true';

    if (authEnabled) {
      if (window.confirm("Are you sure you want to sign out?")) {
        await teardownExperience({ dispatch });
        dispatch(signOut());
      }
    } else {
      // If auth is disabled, just refresh the page or navigate away
      if (window.confirm("Are you sure you want to quit?")) {
        window.location.reload();
      }
    }
  };

  // Show landing page if not authenticated
  if (!isAuthenticated) {
    return <LandingPage />;
  }

  // Defined inline so it can be rendered both behind the LoadingScreen (during initial show,
  // to avoid the device frame flashing behind the modal's fade-in) and in the normal layout.
  const renderSettingsModal = () => (
    <SettingsModal
      onComplete={async (hasChanges = true, configId = null, brandId = null) => {
        isTransitioningRef.current = true;

        if (!hasChanges || !configId || !brandId) {
          setShowSettingsModal(false);
          isTransitioningRef.current = false;
          return;
        }

        try {
          // Full teardown before switching config (user-initiated, not app init).
          // teardownExperience runs the staged Reset sequence so LiveModal,
          // SidePanel, and PegaWebEmbed surfaces are fully unmounted before the
          // new config loads — prevents Pega/Constellation state leaking across
          // configs (source of intermittent React #321 errors).
          dispatch(setCallInProgress(false));
          await teardownExperience({ dispatch });
          // Full teardown before switching config (user-initiated, not app init)
          dispatch(clearDynamicState());
          dispatch(clearNotifications());
          localStorage.removeItem('liveEventHistory');
          localStorage.removeItem('liveEventIndex');
          loadedOnboardingConfigRef.current = null;

          await loadConfigAndBrand(configId, brandId, { skipReset: false });
          setShowSettingsModal(false);
        } catch (err) {
          console.error('❌ Failed to load config:', err);
          setShowSettingsModal(false);
        }

        isTransitioningRef.current = false;
      }}
      onCancel={() => setShowSettingsModal(false)}
    />
  );

  // Hide the device frame behind the SettingsModal only on first launch (before
  // the user has ever seen the device). Once they've started using the app,
  // keep the device visible behind any later SettingsModal openings so it
  // doesn't blink away from under them.
  const hideDeviceBehindModal = showSettingsModal && !hasShownDeviceRef.current;
  if (isAuthenticated && hideDeviceBehindModal) {
    return (
      <>
        <LoadingScreen message="Loading your experience..." />
        <LiveModal />
        {renderSettingsModal()}
      </>
    );
  }

  // Show loading screen while initializing - wait for config AND branding to be fully loaded.
  // Don't show loading screen if we're transitioning from SettingsModal (prevents flash).
  if (isAuthenticated && (!isInitialized || configStatus === 'loading') && !isTransitioningRef.current) {
    return <LoadingScreen message="Loading your experience..." />;
  }

  // Show error screen if config failed to load
  if (isAuthenticated && configStatus === 'failed') {
    return (
      <div className="fixed inset-0 overflow-hidden bg-[#0A0F1A] flex items-center justify-center">
        <div className="text-center px-8 max-w-lg">
          <div className="mb-8">
            <span className="material-icons-outlined text-red-400 text-6xl">error_outline</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Unable to Connect</h2>
          <p className="text-gray-300 mb-8">
            The Pega server is temporarily unavailable. This could be due to maintenance or a network issue.
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gradient-to-r from-[#44C2FD] to-[#23A6D5] text-white font-semibold rounded-lg hover:from-[#3AB0E8] hover:to-[#1E92BA] transition-all duration-300 uppercase"
            >
              Retry
            </button>
            <button
              onClick={() => dispatch(signOut())}
              className="px-6 py-3 bg-transparent text-white font-semibold rounded-lg border-2 border-[#44C2FD] hover:bg-[#44C2FD]/20 transition-all duration-300 uppercase"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // On mobile, render only DeviceFrame (no fullscreen prop)

  return (
    <div className={!isMobile ? "app-container relative h-screen font-inter flex flex-row items-center" : ""}
      style={!isMobile ? { 
        background: `
          radial-gradient(circle at 20% 80%, ${gradientColor4}18 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, ${gradientColor3}18 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, ${gradientColor2}15 0%, transparent 50%),
          linear-gradient(135deg, ${gradientColor1} 0%, ${gradientColor2} 25%, ${gradientColor3} 75%, ${gradientColor4} 100%)
        `,
        backgroundSize: '400% 400%',
        animation: 'gradientShift 20s ease infinite',
        overflow: 'hidden'
      } : {}}>
      {/* Animated background overlay for extra depth - enhanced */}
      {!isMobile && (
        <>
          {/* Subtle animated mesh overlay for premium feel */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.015) 2px, rgba(255, 255, 255, 0.015) 4px),
                repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 255, 255, 0.015) 2px, rgba(255, 255, 255, 0.015) 4px)
              `,
              backgroundSize: '80px 80px',
              opacity: 0.4,
              animation: 'meshMove 20s linear infinite',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          {/* Floating glow orbs */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 60% 60%, ${gradientColor1}12 0%, transparent 70%),
                radial-gradient(circle at 30% 20%, ${gradientColor4}08 0%, transparent 60%)
              `,
              animation: 'float 25s ease-in-out infinite',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          {/* Rotating subtle gradient accent */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                conic-gradient(from 0deg at 50% 50%, ${gradientColor1}00, ${gradientColor2}06, ${gradientColor3}06, ${gradientColor4}00)
              `,
              animation: 'rotate 30s linear infinite',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
        </>
      )}
      {/* Main Content. paddingLeft reserves the fixed left toolbar's width (when
        * it's shown) so the centred device never slides under it. */}
      <div className={!isMobile ? `main-content flex h-full items-center justify-center overflow-hidden flex-grow relative` : ``}
           style={!isMobile ? { position: 'relative', zIndex: 10, paddingLeft: !agentMode ? LEFT_TOOLBAR_WIDTH : 0 } : {}}>
        {/* Device Container - Perfectly Centered */}
        <div className={!isMobile ? `device-container h-full flex items-center justify-center relative` : ``}
          style={!isMobile ? { height: 'min(738px, calc(100dvh - 40px))' } : {}}>
          <div
            className={!isMobile ? `device-frame-container relative` : ``}
            style={!isMobile ? { 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.4)) drop-shadow(0 10px 20px rgba(0, 0, 0, 0.2))',
            } : { 
              width: '100vw', 
              height: '100vh', 
              margin: 0, 
              padding: 0, 
              background: `linear-gradient(135deg, ${gradientColor1}, ${gradientColor2})` 
            }}
          >
            <DeviceFrame 
              customerStatus={effectiveCustomerStatus} 
              liveStatus={effectiveLiveStatus} 
              signalStrength={effectiveSignalStrength}
              forceReconnectAll={forceReconnectAll}
              onQuit={handleQuit}
              isEnvironmentOffline={isEnvironmentOffline}
            />
            
            {isHelpModalOpen && isMobile && <HelpModal />}
          </div>
        </div>
      </div>
      
      {/* Desktop Toast Notifications — plain text under the phone, no box.
        * CSS Grid with every toast in `grid-area: 1 / 1` stacks them all on
        * top of each other so a new toast can cross-fade over the previous
        * one. State machine: 'in' fades up + visible, 'out' fades down + away.
        * (Mobile / FooterCarousel toasts are rendered separately below.) */}
      {!isMobile && toastsEnabled && toastMessages.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '32px',
          // Centre on the phone, which sits between the left toolbar and the
          // right-hand side panel: centre = (toolbar + (100vw - panel)) / 2.
          left: `calc((${LEFT_TOOLBAR_WIDTH}px + 100vw - ${sidePanelWidth}px) / 2)`,
          transform: 'translateX(-50%)',
          // Animate the recentre in step with the side panel's 0.3s width transition.
          transition: 'left 0.3s ease',
          zIndex: 9999,
          pointerEvents: 'none',
          width: 'min(720px, calc(100vw - 80px))',
          display: 'grid',
          justifyItems: 'center',
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0, 0, 0, 0.45)',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          {toastMessages.map((toast) => (
            <div
              key={toast.id}
              style={{
                gridArea: '1 / 1',
                animation: toast.state === 'in'
                  ? 'toastFadeIn 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards'
                  : 'toastFadeOut 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards',
              }}
            >
              {toast.subject && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  color: '#ffffff',
                  lineHeight: 1.3,
                  letterSpacing: '-0.005em',
                }}>
                  {toast.icon && (
                    <span
                      className="material-icons-outlined"
                      style={{
                        fontSize: '13px',
                        color: 'rgba(96, 165, 250, 0.95)',
                        filter: 'drop-shadow(0 0 6px rgba(96, 165, 250, 0.45))',
                      }}
                    >
                      {toast.icon}
                    </span>
                  )}
                  <span>{toast.subject}</span>
                </div>
              )}
              {toast.body && (
                <div style={{
                  fontSize: '11px',
                  fontWeight: 400,
                  color: 'rgba(255, 255, 255, 0.82)',
                  lineHeight: 1.45,
                  marginTop: toast.subject ? '2px' : 0,
                }}>
                  {toast.body}
                </div>
              )}
            </div>
          ))}
          <style>{`
            @keyframes toastFadeIn {
              0%   { opacity: 0; transform: translateY(6px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes toastFadeOut {
              0%   { opacity: 1; transform: translateY(0); }
              100% { opacity: 0; transform: translateY(6px); }
            }
          `}</style>
        </div>
      )}
      
      {/* Customer (brand) logo — top-right corner. The Cara logo lives in the
        * left toolbar. `top` reserves the iOS safe-area inset so it clears the
        * iPad status bar in the iPad-landscape-desktop trial (no-op on desktop). */}
      {!isMobile && brandLogoUrl && (
        <div className="absolute right-4" style={{
          top: 'calc(16px + env(safe-area-inset-top, 0px))',
          zIndex: 20,
          display: 'flex',
          alignItems: 'center',
          // Fade out when the side panel opens (it carries the brand logo in its
          // header then); fade back in when the panel closes.
          opacity: employeeExpanded ? 0 : 1,
          pointerEvents: employeeExpanded ? 'none' : 'auto',
          transition: 'opacity 0.3s ease',
        }}>
          <img
            src={brandLogoUrl}
            alt="Brand Logo"
            className="h-12"
            style={{
              maxWidth: '150px',
              objectFit: 'contain',
              filter: 'brightness(1.1) drop-shadow(0 2px 8px rgba(0,0,0,0.2))',
            }}
          />
        </div>
      )}

      {/* FooterCarousel - only on mobile */}
      {isMobile && <div style={{position: 'relative'}}>
        <ToastContainer
          toastMessages={toastMessages}
          isMobile={true}
          bottom='110px' left='10px' right='10px'
          removeToast={(id) => removeToast(id)}/>
        <FooterCarousel 
          onHomeClick={() => dispatch(setChannel('home'))} 
          onShowSettings={() => setShowSettingsModal(true)}
          onShowBrandSwitcher={() => setShowBrandSwitcherModal(true)}
        />
      </div>}
      {!isMobile && isHelpModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9500 }}>
          <HelpModal />
        </div>
      )}
      
      {/* SettingsModal - unified config and brand selection */}
      {showSettingsModal && renderSettingsModal()}
      
      {/* BrandSwitcher Modal - centered viewport modal for brand switching (mobile only) */}
      {showBrandSwitcherModal && (
        <BrandSwitcher 
          isOpen={showBrandSwitcherModal}
          onClose={() => setShowBrandSwitcherModal(false)}
          mobile={true}
        />
      )}
      
      {/* Collapse button (caret) when expanded - HIDDEN because Chrome window controls are used instead */}
      {/* Expand button (caret) when collapsed - Hide when offline */}
      {!isMobile && employeeVisible && !employeeExpanded && !isEnvironmentOffline && (
        <div style={{
          position: 'fixed',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 75,
        }}>
          <button
            onClick={isAnyModalOpen ? undefined : () => dispatch(toggleSidePanel())}
            title={isAnyModalOpen ? "Cannot expand while modal is open" : "Expand Side Panel"}
            disabled={isAnyModalOpen}
            style={{
              background: isAnyModalOpen ? '#333' : 'var(--cara-caret-bg)',
              borderTop: `1px solid ${isAnyModalOpen ? '#444' : 'var(--cara-caret-border)'}`,
              borderLeft: `1px solid ${isAnyModalOpen ? '#444' : 'var(--cara-caret-border)'}`,
              borderBottom: `1px solid ${isAnyModalOpen ? '#444' : 'var(--cara-caret-border)'}`,
              borderRight: 'none',
              borderRadius: '16px 0 0 16px',
              boxShadow: isAnyModalOpen 
                ? '0 2px 8px rgba(0,0,0,0.12)' 
                : '0 4px 16px rgba(255, 255, 255, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
              padding: '10px 16px',
              cursor: isAnyModalOpen ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 44,
              minHeight: 44,
              opacity: isAnyModalOpen ? 0.5 : 1,
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              if (!isAnyModalOpen) {
                e.target.style.borderTopColor = 'var(--cara-caret-border-hover)';
                e.target.style.borderLeftColor = 'var(--cara-caret-border-hover)';
                e.target.style.borderBottomColor = 'var(--cara-caret-border-hover)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
                e.target.style.background = 'var(--cara-caret-bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isAnyModalOpen) {
                e.target.style.borderTopColor = 'var(--cara-caret-border)';
                e.target.style.borderLeftColor = 'var(--cara-caret-border)';
                e.target.style.borderBottomColor = 'var(--cara-caret-border)';
                e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08)';
                e.target.style.background = 'var(--cara-caret-bg)';
              }
            }}
          >
            {sidePanelIconSrc ? (
              <img 
                src={sidePanelIconSrc} 
                alt="Side Panel"
                style={{
                  width: 32,
                  height: 32,
                  objectFit: 'contain',
                  opacity: isAnyModalOpen ? 0.4 : 1,
                  transition: 'opacity 0.2s ease',
                  filter: isAnyModalOpen ? 'grayscale(100%)' : 'none',
                }}
                onError={(e) => {
                  // Fallback to chevron icon if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : null}
            <span className="material-icons" style={{ 
              fontSize: 32, 
              color: isAnyModalOpen ? '#666' : 'var(--cara-caret-icon)',
              display: sidePanelIconSrc ? 'none' : 'block',
              margin: 0, 
              padding: 0,
              transition: 'color 0.3s ease',
            }}>chevron_left</span>
          </button>
        </div>
      )}
      
      {/* Desktop: Action buttons - pinned in a toolbar on the left edge - Hide on mobile.
        * Position/appearance come from the .desktop-actions-fixed class (left toolbar).
        * The toolbar stays fully visible (no auto-dim). */}
      {!agentMode && !isMobile && (
        <div className="desktop-actions-fixed">
          <Actions
            onShowSettings={() => setShowSettingsModal(true)}
            onShowBrandSwitcher={() => setShowBrandSwitcherModal(true)}
          />
        </div>
      )}
      
      {/* LiveModal - renders at highest z-index when live events occur */}
      <LiveModal />
    </div>
  );
}

function App() {
  const employeeVisible = useSelector((state) => state.channel.channelConfig.employee?.visible !== false);
  const employeeExpanded = useSelector((state) => state.sidePanel.sidePanelExpanded);
  const deviceType = useSelector((state) => state.device.type);
  const windowWidth = useSelector((state) => state.sidePanel.windowWidth);
  const devicePxWidth = deviceType === 'phone' ? 380 : 820;
  const employeePaneWidth = employeeVisible && employeeExpanded
    ? Math.max(windowWidth - devicePxWidth - 200 - LEFT_TOOLBAR_WIDTH, 460)
    : 0;

  // Add enhanced isMobile state
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? shouldUseMobileUI() : false);
  const iPadDesktopLandscape = useSelector((state) => state.channel.iPadDesktopLandscape);

  // Check authentication status
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(shouldUseMobileUI());
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Direct sync when the iPad-landscape-desktop trial flag flips — bypasses
  // the synthetic resize event entirely, so layout reflects the new value on
  // the next render rather than waiting for an actual viewport change.
  React.useEffect(() => {
    setIsMobile(shouldUseMobileUI());
  }, [iPadDesktopLandscape]);

  return (
    <Router>
      {/* Authentication Container - Always rendered for auth modals */}
      <AuthContainer />
      
      {/* Main routed content - always rendered to allow access to public routes like /demos */}
      <div style={{ marginRight: !isMobile && employeeExpanded && employeeVisible && isAuthenticated ? employeePaneWidth : 0, transition: 'margin-right 0.3s', position: 'relative', zIndex: 1 }}>
        <AppRoutes />
      </div>
      
      {/* SidePanel - Desktop: sidebar, Mobile: full-screen overlay - only for authenticated users */}
      {isAuthenticated && employeeVisible && (
        <>
          {/* Desktop SidePanel */}
          {!isMobile && (
                <div
                  className="employee-pane-fixed"
                  style={{
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    height: '100vh',
                    width: employeeExpanded ? employeePaneWidth : 0,
                    zIndex: 50,
                    transition: 'width 0.3s',
                    overflow: 'hidden',
                    pointerEvents: employeeExpanded ? 'auto' : 'none',
                    background: '#111',
                    borderLeft: employeeExpanded ? '1px solid #000' : 'none',
                    // Left-edge drop shadow to lift the panel off the page (3D feel).
                    boxShadow: employeeExpanded ? '-10px 0 30px rgba(0,0,0,0.5)' : 'none',
                  }}
                >
                  <SidePanel expanded={employeeExpanded} maxWidth={employeePaneWidth} />
                </div>
              )}

              {/* Mobile SidePanel - Full-screen overlay */}
              {isMobile && employeeExpanded && (
                <div
                  className="mobile-sidepanel-overlay"
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 10000,
                    background: '#111',
                    overflow: 'hidden',
                    height: '100vh',
                    width: '100vw',
                    paddingTop: 'env(safe-area-inset-top, 0px)',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                  }}
                >
                  <SidePanel expanded={true} maxWidth="100vw" isMobile={true} />
                </div>
              )}
            </>
          )}

      {/* Guided Tour - Auto-starts on first visit */}
      {isAuthenticated && (
        <GuidedTour
          autoStart={true}
          onTourComplete={() => console.log('🎉 Tour completed!')}
          onTourSkip={() => console.log('⏭️ Tour skipped')}
        />
      )}
    </Router>
  );
}

function AboutPage() {
  return (
    <embed
      src="https://labbpublic.s3.eu-west-2.amazonaws.com/Cara.pdf"
      type="application/pdf"
      style={{ width: '100vw', height: '100vh', border: 'none', display: 'block' }}
    />
  );
}

function MeetPage() {
  return (
    <video
      src="https://labbpublic.s3.eu-west-2.amazonaws.com/meet_cara.mp4"
      autoPlay
      loop
      muted
      playsInline
      style={{ width: '100vw', height: '100vh', objectFit: 'cover', display: 'block', background: '#000' }}
    />
  );
}

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    // Remove fullscreenParam logic
  }, [location.search]);

  return (
    <Routes>
      <Route path="/demos" element={<DemosPage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/meet" element={<MeetPage />} />
      <Route path="/:channelName" element={<ChannelRedirect />} />
      <Route path="/" element={<MainApp />} />
    </Routes>
  );
}

export default App;
